import { useEffect, useState } from "react";
import { Euro, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/components/site/EventCard";

// Superadmin-only. "De platform kosten, bijvoorbeeld wat ik aan Mollie kwijt
// ben of wanneer ik Pay.nl inzet." No per-order payment method is tracked
// anywhere in this codebase, so this is a configurable blended-rate ESTIMATE
// (GET/PUT /admin/platform/fee-rates, GET /admin/platform/costs) -- never a
// real, provider-confirmed figure. A rate left at its default (0/0) reports an
// honest "niet ingesteld" state rather than a misleading € 0,00.
//
// Self-fetching, like every other extracted admin panel in this codebase --
// only ever mounted from AdminDashboard.jsx, and only when me.is_superadmin.

const PROVIDER_LABELS = { paynl: "Pay.nl", mollie: "Mollie" };

function centsToEuroInput(cents) {
  return (cents / 100).toFixed(2);
}

function euroInputToCents(value) {
  const n = Number.parseFloat(String(value).replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function bpsToPercentInput(bps) {
  return (bps / 100).toFixed(2);
}

function percentInputToBps(value) {
  const n = Number.parseFloat(String(value).replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function ProviderRateRow({ provider, rate, onSave, saving }) {
  const [percent, setPercent] = useState(bpsToPercentInput(rate.percentage_bps));
  const [fixed, setFixed] = useState(centsToEuroInput(rate.fixed_cents));

  useEffect(() => {
    setPercent(bpsToPercentInput(rate.percentage_bps));
    setFixed(centsToEuroInput(rate.fixed_cents));
  }, [rate.percentage_bps, rate.fixed_cents]);

  const dirty = percentInputToBps(percent) !== rate.percentage_bps || euroInputToCents(fixed) !== rate.fixed_cents;

  return (
    <div className="flex flex-wrap items-end gap-3 py-2 border-b border-svj-border last:border-b-0" data-testid={`platform-fee-rate-${provider}`}>
      <div className="min-w-[90px]">
        <div className="text-sm font-semibold text-white">{PROVIDER_LABELS[provider]}</div>
        {!rate.configured && <div className="text-[11px] text-slate-500">Niet ingesteld</div>}
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">% per transactie</span>
        <input
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={percent}
          onChange={(e) => setPercent(e.target.value)}
          data-testid={`platform-fee-rate-${provider}-percent`}
          className="h-9 w-28 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">Vast per transactie (€)</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={fixed}
          onChange={(e) => setFixed(e.target.value)}
          data-testid={`platform-fee-rate-${provider}-fixed`}
          className="h-9 w-28 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
        />
      </label>
      <button
        type="button"
        disabled={!dirty || saving}
        onClick={() => onSave(provider, { percentage_bps: percentInputToBps(percent), fixed_cents: euroInputToCents(fixed) })}
        data-testid={`platform-fee-rate-${provider}-save`}
        className="h-9 inline-flex items-center gap-1.5 rounded-lg bg-svj-cyan/15 border border-svj-cyan/40 text-svj-cyan px-3 text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-svj-cyan/25 transition-colors"
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        Opslaan
      </button>
    </div>
  );
}

function CostBucket({ label, bucket, testid }) {
  return (
    <div className="rounded-xl border border-svj-border bg-svj-bg px-4 py-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-extrabold text-white">{formatPrice(bucket.estimated_cost_cents)}</div>
      <div className="mt-1 text-xs text-slate-400">
        {bucket.orders} bestelling{bucket.orders === 1 ? "" : "en"} · {formatPrice(bucket.revenue_cents)} omzet
      </div>
    </div>
  );
}

function ByEventTable({ byEvent }) {
  if (!byEvent || byEvent.length === 0) {
    return <p className="text-xs text-slate-500">Nog geen betaalde bestellingen.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-svj-border" data-testid="platform-cost-by-event-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 bg-svj-bg">
            <th className="px-4 py-2 font-semibold">Event</th>
            <th className="px-4 py-2 font-semibold">Account</th>
            <th className="px-4 py-2 font-semibold text-right">Bestellingen</th>
            <th className="px-4 py-2 font-semibold text-right">Omzet</th>
            <th className="px-4 py-2 font-semibold text-right">Geschatte kosten</th>
          </tr>
        </thead>
        <tbody>
          {byEvent.map((ev) => (
            <tr key={ev.event_id || ev.event_title} className="border-t border-svj-border" data-testid={`platform-cost-event-${ev.event_id}`}>
              <td className="px-4 py-2 text-white font-medium">{ev.event_title}</td>
              <td className="px-4 py-2 text-slate-400">
                {ev.is_platform_account ? "Platform" : "Eigen account"} ({PROVIDER_LABELS[ev.provider] || ev.provider})
              </td>
              <td className="px-4 py-2 text-right text-slate-300">{ev.orders}</td>
              <td className="px-4 py-2 text-right text-slate-300">{formatPrice(ev.revenue_cents)}</td>
              <td className="px-4 py-2 text-right text-white font-semibold">{formatPrice(ev.estimated_cost_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PlatformCostsPanel({ authAxios }) {
  const [rates, setRates] = useState(null);
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState(null);

  const load = async () => {
    try {
      const [ratesRes, costsRes] = await Promise.all([
        authAxios.get("/admin/platform/fee-rates"),
        authAxios.get("/admin/platform/costs"),
      ]);
      setRates(ratesRes.data);
      setCosts(costsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveRate = async (provider, rate) => {
    setSavingProvider(provider);
    try {
      const { data } = await authAxios.put("/admin/platform/fee-rates", { [provider]: rate });
      setRates(data);
      // Rates changed -- the cost estimate needs recomputing against them.
      const { data: costsData } = await authAxios.get("/admin/platform/costs");
      setCosts(costsData);
      toast.success(`Tarief voor ${PROVIDER_LABELS[provider]} opgeslagen`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSavingProvider(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-10 rounded-2xl border border-svj-border bg-svj-card p-5 text-center text-sm text-slate-400" data-testid="platform-costs-loading">
        Platformkosten laden…
      </div>
    );
  }
  if (!rates || !costs) return null;

  return (
    <div className="mt-10" data-testid="platform-costs-panel">
      <div className="flex items-center gap-2 mb-3">
        <Euro className="h-4 w-4 text-svj-cyan" />
        <div className="text-sm font-semibold text-white uppercase tracking-wider">Platformkosten (schatting)</div>
      </div>
      <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
        <p className="text-xs text-slate-400">
          Geschatte transactiekosten op basis van je eigen ingestelde tarief per provider — geen echt, door Pay.nl/
          Mollie bevestigd bedrag (er wordt geen betaalmethode per bestelling bijgehouden). Events van een organisatie
          met een eigen Pay.nl/Mollie-account tellen niet mee als platformkosten — die kosten betaalt die organisatie
          zelf, rechtstreeks aan hun eigen account.
        </p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <CostBucket label="Platform-account (Pay.nl)" bucket={costs.platform_account} testid="platform-cost-bucket-platform" />
          <CostBucket label="Organisaties — eigen Pay.nl" bucket={costs.organization_accounts.paynl} testid="platform-cost-bucket-paynl-own" />
          <CostBucket label="Organisaties — eigen Mollie" bucket={costs.organization_accounts.mollie} testid="platform-cost-bucket-mollie-own" />
        </div>

        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Per event</div>
          <ByEventTable byEvent={costs.by_event} />
        </div>

        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Tarief instellen</div>
          <ProviderRateRow provider="paynl" rate={rates.paynl} onSave={saveRate} saving={savingProvider === "paynl"} />
          <ProviderRateRow provider="mollie" rate={rates.mollie} onSave={saveRate} saving={savingProvider === "mollie"} />
        </div>
      </div>
    </div>
  );
}
